/** Pick the sample: a dataset and one of its items, an experiment and one of its results, or an observation. */

import { useId } from "react";
import { Select } from "@/components/ui/select";
import { Segmented } from "@/components/ui/tabs";
import { useDatasetItems, useDatasets } from "@/features/datasets/hooks";
import { sourceName } from "@/features/experiments/status";
import { itemLabel } from "@/lib/item-label";
import { useResultItems } from "./hooks";
import { ObservationPicker } from "@/features/traces/observation-picker";
import { refKey } from "@/features/traces/observation-table";
import type { SampleChoice } from "./use-sample-choice";

function Picker({
  label,
  value,
  onChange,
  empty,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  empty: string;
  options: Array<{ value: string; label: string }>;
}) {
  const id = useId();
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <label htmlFor={id} className="text-xs font-semibold">
        {label}
      </label>
      <Select
        id={id}
        buttonClassName="text-xs"
        value={value}
        onChange={onChange}
        placeholder={empty}
        options={options}
      />
    </div>
  );
}

function DatasetItemPicker({ s }: { s: SampleChoice }) {
  const datasets = useDatasets().data ?? [];
  const version = datasets.find((d) => d.id === s.datasetId)?.latest_version?.version ?? null;
  const items = useDatasetItems(s.datasetId, s.datasetId ? version : null, 1).data?.items ?? [];
  return (
    <div className="flex gap-2">
      <Picker
        label="Dataset"
        value={s.datasetId}
        onChange={s.setDatasetId}
        empty="Choose a dataset…"
        options={datasets.map((d) => ({ value: d.id, label: d.name }))}
      />
      {s.datasetId && (
        <Picker
          label="Item"
          value={s.itemId}
          onChange={s.setItemId}
          empty="Choose an item…"
          options={items.map((i) => ({
            value: i.id,
            label: itemLabel(i.position, i.input),
          }))}
        />
      )}
    </div>
  );
}

function ResultPicker({ s }: { s: SampleChoice }) {
  const results = useResultItems(s.experimentId).data?.items ?? [];
  return (
    <div className="flex gap-2">
      <Picker
        label="Experiment"
        value={s.experimentId}
        onChange={s.setExperimentId}
        empty="Choose an experiment…"
        options={s.experiments.map((e) => ({ value: e.id, label: `${e.name} · ${sourceName(e)}` }))}
      />
      {s.experimentId && (
        <Picker
          label="Result"
          value={s.resultId}
          onChange={s.setResultId}
          empty="Choose a result…"
          options={results.map((r) => ({
            value: r.id,
            label: itemLabel(r.position, r.output_preview),
          }))}
        />
      )}
    </div>
  );
}

function TracePicker({ s }: { s: SampleChoice }) {
  const { trace } = s;
  return (
    <ObservationPicker
      sourceId={trace.connectionId}
      onSource={s.setTraceSource}
      query={trace.filters}
      onQuery={s.setFilters}
      selected={
        trace.traceId ? refKey({ id: trace.traceId, observation_id: trace.observationId || null }) : ""
      }
      onSelect={s.pickTrace}
    />
  );
}

export function SamplePicker({ s }: { s: SampleChoice }) {
  return (
    <div className="flex flex-col gap-3">
      <Segmented
        label="Sample from"
        value={s.source}
        onChange={s.setSource}
        items={[
          { key: "dataset", label: "Dataset item" },
          { key: "result", label: "Past result" },
          { key: "trace", label: "Traces" },
        ]}
      />
      {s.source === "dataset" && <DatasetItemPicker s={s} />}
      {s.source === "result" && <ResultPicker s={s} />}
      {s.source === "trace" && <TracePicker s={s} />}
    </div>
  );
}
